import React from 'react';

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipPayload,
} from 'recharts';

import { RECHARTS_COLORS } from '../../../constants/charts.js';
import {
  CHART_COLORS_IN_ORDER_TAILWIND,
  colorToHex,
  formatLabel,
} from '../../../helpers/charts.js';
import { type ChartProps } from '../../../types/charts.js';
import LinePlotLegend from './LinePlotLegend.jsx';
import LinePlotTooltip from './LinePlotTooltip.jsx';

const CustomLinePlot = ({
  x,
  y,
  dataframe,
  isMobile,
  isAgentic,
  isExport,
}: ChartProps) => {
  const isCompactView = isMobile || isAgentic;

  const colors = CHART_COLORS_IN_ORDER_TAILWIND;

  const renderTooltip = (
    payload: TooltipPayload | undefined,
    active: boolean,
  ) => {
    return (
      <LinePlotTooltip contentData={payload} isActive={!!active} xValue={x} />
    );
  };

  const Chart = (
    <AreaChart
      layout={isCompactView ? 'vertical' : 'horizontal'}
      width={700}
      height={400}
      data={dataframe}
      margin={{
        top: isCompactView ? 50 : 10,
        right: 30,
        bottom: isCompactView ? 40 : 30,
        left: 20,
      }}
    >
      <CartesianGrid
        strokeDasharray="3 3"
        stroke={RECHARTS_COLORS.BORDER_PRIMARY}
      />
      <XAxis
        dataKey={isCompactView ? undefined : x}
        type={isCompactView ? 'number' : 'category'}
        tickCount={dataframe.length}
        fontSize={12}
        label={
          isCompactView
            ? {}
            : {
                value: formatLabel({
                  label: x,
                }),
                position: 'insideBottom',
                fill: RECHARTS_COLORS.TEXT_PRIMARY,
                offset: -10,
              }
        }
      />
      <YAxis
        tickCount={dataframe.length}
        fontSize={12}
        dataKey={isCompactView ? x : undefined}
        type={isCompactView ? 'category' : 'number'}
        label={
          isCompactView
            ? {
                value: formatLabel({
                  label: x,
                }),
                position: 'insideTop',
                fill: RECHARTS_COLORS.TEXT_PRIMARY,
                offset: -30,
              }
            : {}
        }
      />
      {!isExport && (
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={({ payload, active }) => renderTooltip(payload, !!active)}
        />
      )}

      {y.map((yItem, i) => {
        return (
          <Area
            key={yItem}
            type="monotone"
            dataKey={yItem}
            stroke={colorToHex(colors[i % colors.length])}
            fill={colorToHex(colors[i % colors.length])}
            isAnimationActive={false}
          />
        );
      })}
    </AreaChart>
  );

  if (isCompactView) {
    return (
      <>
        <ResponsiveContainer width="100%" height={700}>
          {Chart}
        </ResponsiveContainer>
        <LinePlotLegend y={y} colors={colors} />
      </>
    );
  }

  return (
    <div className="flex w-full flex-col items-center justify-center">
      {Chart}
      <LinePlotLegend y={y} colors={colors} />
    </div>
  );
};

export default CustomLinePlot;
